import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AddressLookup } from '@/components/address-lookup';
import { Badge } from '@/components/ui/badge';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft,
  MapPin,
  Package,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Save,
  X,
} from 'lucide-react';
import { mockLocations } from '@/lib/mock-settings-data';
import { mockAssets } from '@/lib/mock-data';
import type { Location, SubLocation } from '@/types/settings';
import { useMode } from '@/providers/ModeProvider';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { useToast } from '@/hooks/use-toast';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper function to create a new empty location - extracted to reduce cognitive complexity (S3776)
function createNewEmptyLocation(): Location {
  return {
    id: `new-${Date.now()}`,
    name: '',
    code: '',
    notes: '',
    address: '',
    locationManager: '',
    assetCount: 0,
    createdBy: 'current-user',
    createdDate: new Date().toISOString(),
    subLocations: [],
  };
}

// Helper function to geocode address (simplified - in production use a proper geocoding service)
const geocodeAddress = async (address: string): Promise<[number, number] | null> => {
  try {
    // Using Nominatim (OpenStreetMap's geocoding service) - free and open source
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
    );
    const data = await response.json();
    if (data?.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
  return null;
};

export default function LocationDetail() {
  const { locationId } = useParams<{ locationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isDemo } = useMode();
  const backendUrl = useGetBackendUrl();

  const [location, setLocation] = useState<Location | null>(null);
  const [editedLocation, setEditedLocation] = useState<Location | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSubLocationDialog, setShowSubLocationDialog] = useState(false);
  const [editingSubLocation, setEditingSubLocation] = useState<SubLocation | null>(null);
  const [subLocationFormData, setSubLocationFormData] = useState<Partial<SubLocation>>({
    name: '',
    notes: '',
    locationManager: '',
  });
  const [mapCoordinates, setMapCoordinates] = useState<[number, number] | null>(null);
  const [isGeocodingComplete, setIsGeocodingComplete] = useState(false);

  // Check if we're in create mode
  const isCreateMode = searchParams.get('mode') === 'create';

  useEffect(() => {
    if (isCreateMode) {
      // Use extracted helper function to reduce cognitive complexity (S3776)
      const newLocation = createNewEmptyLocation();
      setLocation(newLocation);
      setEditedLocation(newLocation);
      setIsEditMode(true);
      setLoading(false);
    } else {
      loadLocationDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, isCreateMode]);

  // Geocode address when it changes
  useEffect(() => {
    const currentLocation = isEditMode ? editedLocation : location;
    setIsGeocodingComplete(false);
    
    if (currentLocation?.address) {
      geocodeAddress(currentLocation.address).then((coords) => {
        if (coords) {
          setMapCoordinates(coords);
        } else {
          // If geocoding fails, default to UK center
          setMapCoordinates([54.5, -4.0]);
        }
        setIsGeocodingComplete(true);
      });
    } else {
      // Default to UK center
      setMapCoordinates([54.5, -4.0]);
      setIsGeocodingComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.address, editedLocation?.address, isEditMode]);

  const loadLocationDetail = async () => {
    if (!locationId) return;

    setLoading(true);
    try {
      if (!isDemo) {
        const response = await fetch(`${backendUrl}/api/Settings/locations/${locationId}`);
        if (response.ok) {
          const data = await response.json();
          setLocation(data);
        } else {
          throw new Error('Failed to load location');
        }
      } else {
        // Demo mode - find from mock data
        const mockLocation = mockLocations.find((loc) => loc.id === locationId);
        if (mockLocation) {
          // Calculate asset counts
          const assetCount = mockAssets.filter((asset) => asset.location === mockLocation.name).length;
          const subLocationsWithCounts = (mockLocation.subLocations ?? []).map((subLoc) => ({
            ...subLoc,
            assetCount: mockAssets.filter(
              (asset) => asset.location === mockLocation.name && asset.subLocation === subLoc.name
            ).length,
          }));
          setLocation({
            ...mockLocation,
            assetCount,
            subLocations: subLocationsWithCounts,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load location:', error);
      toast({
        title: 'Error',
        description: 'Failed to load location details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditedLocation(location);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreateMode) {
      navigate('/administration/locations');
    } else {
      setEditedLocation(null);
      setIsEditMode(false);
    }
  };

  const createLocationApi = async (locationData: Location) => {
        const response = await fetch(`${backendUrl}/api/Settings/locations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationData),
        });
    if (!response.ok) {
      throw new Error('Failed to create location');
    }
    return response.json();
  };

  const updateLocationApi = async (locationData: Location) => {
        const response = await fetch(`${backendUrl}/api/Settings/locations/${locationData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationData),
        });
    if (!response.ok) {
      throw new Error('Failed to update location');
    }
  };

  const handleSave = async () => {
    if (!editedLocation || !editedLocation.name) {
      toast({
        title: 'Error',
        description: 'Location name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      if (!isDemo) {
        if (isCreateMode) {
          const createdLocation = await createLocationApi(editedLocation);
          toast({ title: 'Success', description: 'Location created successfully' });
          navigate(`/administration/locations/${createdLocation.id}`, { replace: true });
        } else {
          await updateLocationApi(editedLocation);
          toast({ title: 'Success', description: 'Location updated successfully' });
          await loadLocationDetail();
        }
      } else {
        toast({
          title: 'Success',
          description: isCreateMode ? 'Location created successfully' : 'Location updated successfully',
        });
        if (isCreateMode) {
          navigate('/administration/locations');
        } else {
          setLocation(editedLocation);
        }
      }
      setIsEditMode(false);
      setEditedLocation(null);
    } catch (error) {
      console.error('Failed to save location:', error);
      toast({
        title: 'Error',
        description: 'Failed to save location. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!location) return;

    try {
      if (!isDemo) {
                const response = await fetch(`${backendUrl}/api/Settings/locations/${location.id}`, {
                  method: 'DELETE',
                  credentials: 'include',
                });
        if (response.ok) {
          toast({
            title: 'Success',
            description: 'Location deleted successfully',
          });
          navigate('/administration/locations');
        } else {
          // Try to get the error message from the response
          let errorMessage = 'Failed to delete location. Please try again.';
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch {
            // If parsing fails, use default message
          }
          
          toast({
            title: 'Error',
            description: errorMessage,
            variant: 'destructive',
          });
          return; // Don't navigate away on error
        }
      } else {
        toast({
          title: 'Success',
          description: 'Location deleted successfully',
        });
        navigate('/administration/locations');
      }
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete location. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleLocationUpdate = (field: keyof Location, value: Location[keyof Location]) => {
    if (isEditMode && editedLocation) {
      setEditedLocation({ ...editedLocation, [field]: value });
    }
  };

  const handleOpenSubLocationDialog = (subLocation?: SubLocation) => {
    if (subLocation) {
      setEditingSubLocation(subLocation);
      setSubLocationFormData(subLocation);
    } else {
      setEditingSubLocation(null);
      setSubLocationFormData({
        name: '',
        notes: '',
        locationManager: '',
      });
    }
    setShowSubLocationDialog(true);
  };

  const updateSubLocationApi = async (subLocationId: string, data: Partial<SubLocation>) => {
        const response = await fetch(`${backendUrl}/api/Settings/sublocations/${subLocationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
    if (!response.ok) {
      throw new Error('Failed to update sub-location');
    }
  };

  const createSubLocationApi = async (locationId: string, data: Partial<SubLocation>) => {
        const response = await fetch(`${backendUrl}/api/Settings/locations/${locationId}/sublocations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
    if (!response.ok) {
      throw new Error('Failed to create sub-location');
    }
  };

  const createDemoSubLocation = (currentLocation: Location): SubLocation => {
    if (editingSubLocation) {
      return { ...editingSubLocation, ...subLocationFormData, modifiedDate: new Date().toISOString() };
    }
    return {
      id: `sub-${Date.now()}`,
      name: subLocationFormData.name ?? '',
      notes: subLocationFormData.notes,
      locationManager: subLocationFormData.locationManager,
      assetCount: 0,
      parentLocationId: currentLocation.id,
      createdBy: 'current-user',
      createdDate: new Date().toISOString(),
    };
  };

  const updateLocationState = (currentLocation: Location, updatedSubLocations: SubLocation[]) => {
    if (isEditMode) {
      setEditedLocation({ ...currentLocation, subLocations: updatedSubLocations });
    } else {
      setLocation({ ...currentLocation, subLocations: updatedSubLocations });
    }
  };

  const handleSaveSubLocation = async () => {
    if (!subLocationFormData.name) {
      toast({ title: 'Error', description: 'Sub-location name is required', variant: 'destructive' });
      return;
    }

    const currentLocation = isEditMode ? editedLocation : location;
    if (!currentLocation) return;

    try {
      if (!isDemo) {
        if (editingSubLocation) {
          await updateSubLocationApi(editingSubLocation.id, subLocationFormData);
        } else {
          await createSubLocationApi(currentLocation.id, subLocationFormData);
        }
        await loadLocationDetail();
      } else {
        const newSubLocation = createDemoSubLocation(currentLocation);
        const updatedSubLocations = editingSubLocation
          ? (currentLocation.subLocations ?? []).map((sub) =>
              sub.id === editingSubLocation.id ? newSubLocation : sub
            )
          : [...(currentLocation.subLocations ?? []), newSubLocation];
        updateLocationState(currentLocation, updatedSubLocations);
      }
      toast({ title: 'Success', description: editingSubLocation ? 'Sub-location updated successfully' : 'Sub-location created successfully' });
      setShowSubLocationDialog(false);
    } catch (error) {
      console.error('Failed to save sub-location:', error);
      toast({ title: 'Error', description: 'Failed to save sub-location. Please try again.', variant: 'destructive' });
    }
  };

  const handleDeleteSubLocation = async (subLocationId: string) => {
    const currentLocation = isEditMode ? editedLocation : location;
    if (!currentLocation) return;

    try {
      if (!isDemo) {
                const response = await fetch(`${backendUrl}/api/Settings/sublocations/${subLocationId}`, {
                  method: 'DELETE',
                  credentials: 'include',
                });
        if (response.ok) {
          await loadLocationDetail();
          toast({
            title: 'Success',
            description: 'Sub-location deleted successfully',
          });
        } else {
          // Try to get the error message from the response
          let errorMessage = 'Failed to delete sub-location. Please try again.';
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch {
            // If parsing fails, use default message
          }
          
          toast({
            title: 'Error',
            description: errorMessage,
            variant: 'destructive',
          });
          return; // Don't continue on error
        }
      } else {
        // Demo mode
        const updatedSubLocations = (currentLocation.subLocations ?? []).filter(
          (sub) => sub.id !== subLocationId
        );
        if (isEditMode) {
          setEditedLocation({ ...currentLocation, subLocations: updatedSubLocations });
        } else {
          setLocation({ ...currentLocation, subLocations: updatedSubLocations });
        }
        toast({
          title: 'Success',
          description: 'Sub-location deleted successfully',
        });
      }
    } catch (error) {
      console.error('Failed to delete sub-location:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete sub-location. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAssetCountClick = (subLocationName?: string) => {
    if (!location) return;
    if (subLocationName) {
      navigate(`/assets?location=${encodeURIComponent(location.name)}&subLocation=${encodeURIComponent(subLocationName)}`);
    } else {
      navigate(`/assets?location=${encodeURIComponent(location.name)}`);
    }
  };

  const currentLocation = isEditMode ? editedLocation : location;

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="border-b bg-card p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-6 w-96" />
        </div>
        <div className="flex-1 flex">
          <div className="flex-1 p-6">
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!location && !isCreateMode) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Location Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The location you're looking for doesn't exist or has been deleted.
          </p>
          <Button onClick={() => navigate('/administration/locations')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Locations
          </Button>
        </div>
      </div>
    );
  }

  if (!currentLocation) return null;

  const subLocationsCount = currentLocation.subLocations?.length ?? 0;

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="border-b bg-card">
          <div className="px-6 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/administration/locations')}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <MapPin className="h-6 w-6 text-primary" />
                  <div>
                    {isEditMode ? (
                      <Input
                        value={currentLocation.name}
                        onChange={(e) => handleLocationUpdate('name', e.target.value)}
                        className="text-2xl font-bold h-auto border-0 px-0 focus-visible:ring-0"
                        placeholder="Location Name"
                      />
                    ) : (
                      <h1 className="text-2xl font-bold">{currentLocation.name}</h1>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        <Package className="h-3 w-3 mr-1" />
                        {currentLocation.assetCount ?? 0} Assets
                      </Badge>
                      {subLocationsCount > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {subLocationsCount} Sub-Locations
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isEditMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {isCreateMode ? 'Create Location' : 'Save Changes'}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Details */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border">
                  <CardHeader>
                    <CardTitle>Location Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Location Name *</Label>
                          {isEditMode ? (
                            <Input
                              id="name"
                              value={currentLocation.name}
                              onChange={(e) => handleLocationUpdate('name', e.target.value)}
                              placeholder="e.g., Head Office"
                            />
                          ) : (
                            <Input
                              id="name"
                              value={currentLocation.name || '-'}
                              readOnly
                              className="bg-muted"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="code">Location Code</Label>
                          {isEditMode ? (
                            <Input
                              id="code"
                              value={currentLocation.code ?? ''}
                              onChange={(e) => handleLocationUpdate('code', e.target.value)}
                              placeholder="e.g., HQ-01"
                            />
                          ) : (
                            <Input
                              id="code"
                              value={currentLocation.code || '-'}
                              readOnly
                              className="bg-muted"
                            />
                          )}
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="address">Address</Label>
                          {isEditMode ? (
                            <AddressLookup
                              value={currentLocation.address ?? ''}
                              onChange={(value) => handleLocationUpdate('address', value)}
                              placeholder="Start typing to search for an address..."
                            />
                          ) : (
                            <Input
                              id="address"
                              value={currentLocation.address ?? '-'}
                              readOnly
                              className="bg-muted"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="manager">Location Manager</Label>
                          {isEditMode ? (
                            <Input
                              id="manager"
                              value={currentLocation.locationManager ?? ''}
                              onChange={(e) => handleLocationUpdate('locationManager', e.target.value)}
                              placeholder="e.g., John Smith"
                            />
                          ) : (
                            <Input
                              id="manager"
                              value={currentLocation.locationManager ?? '-'}
                              readOnly
                              className="bg-muted"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Asset Count</Label>
                          {isCreateMode ? (
                            <div className="inline-flex items-center gap-2 w-full justify-start px-3 py-2 rounded-md border bg-muted text-muted-foreground cursor-not-allowed">
                              <Package className="h-4 w-4" />
                              <span className="text-sm font-medium">{currentLocation.assetCount ?? 0} Assets</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleAssetCountClick()}
                              className="inline-flex items-center gap-2 text-primary hover:underline cursor-pointer w-full justify-start px-3 py-2 rounded-md border bg-muted"
                            >
                              <Package className="h-4 w-4" />
                              <span className="text-sm font-medium">{currentLocation.assetCount ?? 0} Assets</span>
                            </button>
                          )}
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="notes">Notes</Label>
                          {isEditMode ? (
                            <Textarea
                              id="notes"
                              value={currentLocation.notes ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleLocationUpdate('notes', e.target.value)}
                              placeholder="Additional notes about this location"
                              rows={4}
                            />
                          ) : (
                            <Textarea
                              id="notes"
                              value={currentLocation.notes ?? '-'}
                              readOnly
                              className="bg-muted resize-none"
                              rows={4}
                            />
                          )}
                        </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sub-Locations Card */}
                {!isCreateMode && (
                  <Card className="border">
                    <CardHeader className="pb-[15px]">
                      <div className="flex items-center justify-between">
                        <CardTitle>Sub-Locations</CardTitle>
                        <Button size="sm" onClick={() => handleOpenSubLocationDialog()}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Sub-Location
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 border-t">
                      {currentLocation.subLocations && currentLocation.subLocations.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="pl-6">Name</TableHead>
                              <TableHead>Manager</TableHead>
                              <TableHead>Notes</TableHead>
                              <TableHead className="text-center">Assets</TableHead>
                              <TableHead className="text-right pr-6">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {currentLocation.subLocations.map((subLoc) => (
                              <TableRow key={subLoc.id}>
                                <TableCell className="font-medium pl-6">{subLoc.name}</TableCell>
                                <TableCell>{subLoc.locationManager ?? '-'}</TableCell>
                                <TableCell className="max-w-xs truncate">{subLoc.notes ?? '-'}</TableCell>
                                <TableCell className="text-center">
                                  <button
                                    onClick={() => handleAssetCountClick(subLoc.name)}
                                    className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                                  >
                                    <Package className="h-3 w-3" />
                                    <Badge variant="secondary">{subLoc.assetCount ?? 0}</Badge>
                                  </button>
                                </TableCell>
                                <TableCell className="text-right pr-6">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleOpenSubLocationDialog(subLoc)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        if (confirm('Are you sure you want to delete this sub-location?')) {
                                          handleDeleteSubLocation(subLoc.id);
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-12 text-muted-foreground">
                          <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p className="font-medium">No sub-locations yet</p>
                          <p className="text-sm mt-1">Click "Add Sub-Location" to create one</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column - Map */}
              <div className="lg:col-span-1">
                <Card className="border sticky top-6 overflow-hidden p-0" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
                  <div className="w-full" style={{ height: 'calc(100vh - 12rem)' }}>
                        {mapCoordinates && isGeocodingComplete ? (
                          <MapContainer
                            key={`${mapCoordinates[0]}-${mapCoordinates[1]}`}
                            center={mapCoordinates}
                            zoom={currentLocation.address ? 15 : 6}
                            style={{ height: '100%', width: '100%' }}
                            className="rounded-b-lg"
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {currentLocation.address && (
                              <Marker position={mapCoordinates}>
                                <Popup>
                                  <div className="p-2">
                                    <p className="font-semibold">{currentLocation.name}</p>
                                    <p className="text-sm text-muted-foreground">{currentLocation.address}</p>
                                  </div>
                                </Popup>
                              </Marker>
                            )}
                          </MapContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground bg-muted/30">
                            <div className="text-center p-4">
                              <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">{!isGeocodingComplete ? 'Loading map...' : 'No address available'}</p>
                            </div>
                          </div>
                        )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this location? This will also delete all sub-locations. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sub-Location Dialog */}
      <Dialog open={showSubLocationDialog} onOpenChange={setShowSubLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubLocation ? 'Edit Sub-Location' : 'Add Sub-Location'}</DialogTitle>
            <DialogDescription>
              {editingSubLocation
                ? 'Update the sub-location details below.'
                : `Add a new sub-location to ${currentLocation.name}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subLocationName">Sub-Location Name *</Label>
              <Input
                id="subLocationName"
                value={subLocationFormData.name ?? ''}
                onChange={(e) => setSubLocationFormData({ ...subLocationFormData, name: e.target.value })}
                placeholder="e.g., Floor 2, Building A"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subLocationManager">Location Manager</Label>
              <Input
                id="subLocationManager"
                value={subLocationFormData.locationManager ?? ''}
                onChange={(e) => setSubLocationFormData({ ...subLocationFormData, locationManager: e.target.value })}
                placeholder="e.g., Jane Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subLocationNotes">Notes</Label>
              <Textarea
                id="subLocationNotes"
                value={subLocationFormData.notes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSubLocationFormData({ ...subLocationFormData, notes: e.target.value })}
                placeholder="Additional notes about this sub-location"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubLocationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubLocation} disabled={!subLocationFormData.name}>
              {editingSubLocation ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
